import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  FolderOpen, 
  Upload, 
  FileText, 
  Image as ImageIcon, 
  Download, 
  Trash2, 
  Loader2, 
  Plus,
  X,
  CheckCircle,
  AlertCircle,
  HardDrive,
  Cloud
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ScreenHelp } from '../components/common/ScreenHelp';
import PageHeader from '../components/common/PageHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FOLDER_INFO = {
  capa: { label: 'Capa', icon: ImageIcon, description: 'Imagem de capa da viagem', color: 'text-purple-500' },
  fotos: { label: 'Fotos', icon: ImageIcon, description: 'Galeria de fotos', color: 'text-blue-500' },
  arquivos: { label: 'Arquivos', icon: FileText, description: 'Documentos e arquivos', color: 'text-green-500' }
};

const UploadStatus = {
  PENDING: 'pending',
  UPLOADING: 'uploading',
  SUCCESS: 'success',
  ERROR: 'error'
};

function formatFileSize(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function FileUploadItem({ file, status, progress, onRemove }) {
  const isImage = file.type.startsWith('image/');
  
  return (
    <div className="flex items-center gap-3 p-2 bg-slate-50 dark:bg-slate-800 rounded-lg">
      <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
        {isImage && file.preview ? (
          <img src={file.preview} alt={file.name} className="w-full h-full object-cover" />
        ) : isImage ? (
          <ImageIcon className="w-5 h-5 text-slate-400" />
        ) : (
          <FileText className="w-5 h-5 text-slate-400" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{file.name}</p>
        <p className="text-xs text-slate-500">{formatFileSize(file.size)}</p>
        {status === UploadStatus.UPLOADING && (
          <Progress value={progress} className="h-1 mt-1" />
        )}
      </div>
      <div className="flex-shrink-0">
        {status === UploadStatus.PENDING && (
          <Button variant="ghost" size="sm" onClick={onRemove} className="h-8 w-8 p-0">
            <X className="w-4 h-4" />
          </Button>
        )}
        {status === UploadStatus.UPLOADING && (
          <Loader2 className="w-5 h-5 text-orange-500 animate-spin" />
        )}
        {status === UploadStatus.SUCCESS && (
          <CheckCircle className="w-5 h-5 text-green-500" />
        )}
        {status === UploadStatus.ERROR && (
          <AlertCircle className="w-5 h-5 text-red-500" />
        )}
      </div>
    </div>
  );
}

function FileCard({ file, tripId, token, onDelete, isOrganizer }) {
  const [downloading, setDownloading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const isImage = file.content_type.startsWith('image/');

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${API_URL}/api/storage/trips/${tripId}/files/${file.id}/download-url`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Erro ao obter URL');

      const { download_url, filename } = await res.json();
      
      const link = document.createElement('a');
      link.href = download_url;
      link.download = filename;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      toast.error('Erro ao baixar arquivo');
    } finally {
      setDownloading(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await onDelete(file.id);
      setShowDeleteDialog(false);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="flex items-center gap-4 p-4 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-orange-300 dark:hover:border-orange-500/50 transition-colors">
      {/* Icon */}
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
        isImage ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-green-100 dark:bg-green-900/30'
      }`}>
        {isImage ? (
          <ImageIcon className="w-6 h-6 text-blue-500" />
        ) : (
          <FileText className="w-6 h-6 text-green-500" />
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-slate-900 dark:text-white truncate">
          {file.original_filename}
        </p>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <span className="text-xs text-slate-500">{file.file_size_formatted}</span>
          <span className="text-xs text-slate-400">•</span>
          <span className="text-xs text-slate-500">
            {format(new Date(file.created_at), "dd/MM/yy HH:mm", { locale: ptBR })}
          </span>
          <Badge variant="outline" className="text-xs capitalize">
            {FOLDER_INFO[file.folder]?.label || file.folder}
          </Badge>
        </div>
        <p className="text-xs text-slate-400 mt-1">
          por {file.uploaded_by_name}
        </p>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 flex-shrink-0">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleDownload}
          disabled={downloading}
          className="h-9 w-9 p-0"
          title="Baixar"
        >
          {downloading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Download className="w-4 h-4" />
          )}
        </Button>
        
        {isOrganizer && (
          <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowDeleteDialog(true)}
              className="h-9 w-9 p-0 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
              title="Excluir"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Excluir arquivo?</AlertDialogTitle>
                <AlertDialogDescription>
                  &quot;{file.original_filename}&quot; será removido permanentemente.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDelete}
                  disabled={deleting}
                  className="bg-red-500 hover:bg-red-600"
                >
                  {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Excluir'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    </div>
  );
}

export default function TripFiles() {
  const { tripId } = useParams();
  const context = useOutletContext();
  const { trip, isOrganizer } = context || {};
  const { token } = useAuth();

  const [activeFolder, setActiveFolder] = useState('arquivos');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [storageUsage, setStorageUsage] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Fetch files
  const fetchFiles = async (folder = null) => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/storage/trips/${tripId}/files`;
      if (folder) url += `?folder=${folder}`;

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setFiles(data);
      } else if (res.status === 503) {
        toast.error('Serviço de armazenamento não configurado');
        setFiles([]);
      }
    } catch (error) {
      console.error('Error fetching files:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch storage usage
  const fetchStorageUsage = async () => {
    if (!isOrganizer) return;
    try {
      const res = await fetch(`${API_URL}/api/storage/trips/${tripId}/storage-usage`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setStorageUsage(await res.json());
      }
    } catch (error) {
      console.error('Error fetching storage usage:', error);
    }
  };

  useEffect(() => {
    fetchFiles(activeFolder);
    fetchStorageUsage();
  }, [tripId, activeFolder, token]);

  // Dropzone
  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    rejectedFiles.forEach(({ file, errors }) => {
      errors.forEach(error => {
        if (error.code === 'file-too-large') {
          toast.error(`${file.name}: Arquivo muito grande (máx. 100MB)`);
        }
      });
    });

    const newFiles = acceptedFiles.map(file => ({
      file,
      id: `${file.name}-${Date.now()}`,
      name: file.name,
      size: file.size,
      type: file.type,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
      status: UploadStatus.PENDING,
      progress: 0
    }));

    setPendingFiles(prev => [...prev, ...newFiles].slice(0, 20));
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxSize: 100 * 1024 * 1024,
    maxFiles: 20 - pendingFiles.length,
    disabled: uploading || pendingFiles.length >= 20
  });

  const removeFile = (id) => {
    setPendingFiles(prev => {
      const file = prev.find(f => f.id === id);
      if (file?.preview) URL.revokeObjectURL(file.preview);
      return prev.filter(f => f.id !== id);
    });
  };

  const uploadFiles = async () => {
    if (pendingFiles.length === 0 || uploading) return;

    setUploading(true);
    const pending = pendingFiles.filter(f => f.status === UploadStatus.PENDING);
    let successCount = 0;

    for (const fileObj of pending) {
      try {
        setPendingFiles(prev => prev.map(f => 
          f.id === fileObj.id ? { ...f, status: UploadStatus.UPLOADING, progress: 0 } : f
        ));

        // Get presigned URL
        const urlRes = await fetch(`${API_URL}/api/storage/trips/${tripId}/upload-url`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            filename: fileObj.name,
            content_type: fileObj.type,
            folder: activeFolder
          })
        });

        if (!urlRes.ok) throw new Error('Erro ao obter URL');
        const { upload_url, file_id } = await urlRes.json();

        setPendingFiles(prev => prev.map(f => 
          f.id === fileObj.id ? { ...f, progress: 30 } : f
        ));

        // Upload to R2
        const uploadRes = await fetch(upload_url, {
          method: 'PUT',
          headers: { 'Content-Type': fileObj.type },
          body: fileObj.file
        });

        if (!uploadRes.ok) throw new Error('Erro no upload');

        setPendingFiles(prev => prev.map(f => 
          f.id === fileObj.id ? { ...f, progress: 70 } : f
        ));

        // Confirm
        const confirmRes = await fetch(`${API_URL}/api/storage/trips/${tripId}/confirm-upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ file_id, file_size_bytes: fileObj.size })
        });

        if (!confirmRes.ok) throw new Error('Erro ao confirmar');

        setPendingFiles(prev => prev.map(f => 
          f.id === fileObj.id ? { ...f, status: UploadStatus.SUCCESS, progress: 100 } : f
        ));
        successCount++;

      } catch (error) {
        console.error('Upload error:', error);
        setPendingFiles(prev => prev.map(f => 
          f.id === fileObj.id ? { ...f, status: UploadStatus.ERROR, progress: 0 } : f
        ));
      }
    }

    setUploading(false);

    if (successCount > 0) {
      toast.success(`${successCount} arquivo(s) enviado(s)!`);
      fetchFiles(activeFolder);
      fetchStorageUsage();
      
      await new Promise(resolve => setTimeout(resolve, 1500));
      setPendingFiles(prev => prev.filter(f => f.status !== UploadStatus.SUCCESS));
      setUploadDialogOpen(false);
    }
  };

  const handleDelete = async (fileId) => {
    try {
      const res = await fetch(`${API_URL}/api/storage/trips/${tripId}/files/${fileId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Arquivo excluído');
        setFiles(prev => prev.filter(f => f.id !== fileId));
        fetchStorageUsage();
      } else {
        throw new Error('Erro ao excluir');
      }
    } catch (error) {
      toast.error('Erro ao excluir arquivo');
    }
  };

  const pendingCount = pendingFiles.filter(f => f.status === UploadStatus.PENDING).length;
  const filteredFiles = files.filter(f => activeFolder === 'all' || f.folder === activeFolder);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Arquivos"
        subtitle="Gerenciar arquivos da viagem"
        badge={
          <Badge variant="outline" className="text-xs border-blue-500/50 text-blue-600 dark:text-blue-400">
            <Cloud className="w-3 h-3 mr-1" />
            Cloudflare R2
          </Badge>
        }
        actions={
          <div className="flex items-center gap-2">
            <ScreenHelp screen="files" />
            <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-orange-500 hover:bg-orange-600" data-testid="upload-files-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Enviar Arquivos
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>
                    Enviar para {FOLDER_INFO[activeFolder]?.label || 'Arquivos'}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div
                    {...getRootProps()}
                    className={`
                      border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors
                      ${isDragActive 
                        ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20' 
                        : 'border-slate-300 dark:border-slate-600 hover:border-orange-400'
                      }
                      ${uploading || pendingFiles.length >= 20 ? 'opacity-50 cursor-not-allowed' : ''}
                    `}
                  >
                    <input {...getInputProps()} data-testid="r2-file-input" />
                    <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                      {isDragActive ? 'Solte os arquivos aqui...' : 'Arraste arquivos ou clique para selecionar'}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      Até 100MB por arquivo • Máximo 20 arquivos
                    </p>
                  </div>

                  {pendingFiles.length > 0 && (
                    <div className="max-h-48 overflow-y-auto space-y-2">
                      {pendingFiles.map(file => (
                        <FileUploadItem
                          key={file.id}
                          file={file}
                          status={file.status}
                          progress={file.progress}
                          onRemove={() => removeFile(file.id)}
                        />
                      ))}
                    </div>
                  )}

                  {pendingCount > 0 && (
                    <Button
                      onClick={uploadFiles}
                      disabled={uploading}
                      className="w-full bg-orange-500 hover:bg-orange-600"
                      data-testid="confirm-upload-btn"
                    >
                      {uploading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Enviando...
                        </>
                      ) : (
                        <>
                          <Cloud className="w-4 h-4 mr-2" />
                          Enviar {pendingCount} arquivo(s)
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      {/* Storage Usage Card */}
      {isOrganizer && storageUsage && (
        <Card className="border-orange-200 dark:border-orange-500/30 bg-gradient-to-r from-orange-50 to-transparent dark:from-orange-900/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                  <HardDrive className="w-5 h-5 text-orange-500" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900 dark:text-white">
                    Uso de Armazenamento
                  </p>
                  <p className="text-xs text-slate-500">
                    {storageUsage.total_files} arquivos
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xl font-bold text-orange-500">
                  {storageUsage.total_size_formatted}
                </p>
                <div className="flex gap-2 mt-1">
                  {Object.entries(storageUsage.by_folder || {}).map(([folder, data]) => (
                    <Badge key={folder} variant="outline" className="text-xs">
                      {FOLDER_INFO[folder]?.label || folder}: {data.files}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Folder Tabs */}
      <Tabs value={activeFolder} onValueChange={setActiveFolder}>
        <TabsList className="grid w-full grid-cols-3">
          {Object.entries(FOLDER_INFO).map(([key, info]) => {
            const Icon = info.icon;
            const count = files.filter(f => f.folder === key).length;
            return (
              <TabsTrigger key={key} value={key} className="gap-2">
                <Icon className={`w-4 h-4 ${info.color}`} />
                {info.label}
                {count > 0 && (
                  <Badge variant="secondary" className="ml-1 text-xs">
                    {count}
                  </Badge>
                )}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {Object.keys(FOLDER_INFO).map(folder => (
          <TabsContent key={folder} value={folder} className="mt-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
              </div>
            ) : filteredFiles.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <FolderOpen className="w-12 h-12 text-slate-300 dark:text-slate-600 mb-4" />
                  <p className="text-slate-500 dark:text-slate-400 text-center">
                    Nenhum arquivo em {FOLDER_INFO[folder]?.label}
                  </p>
                  <p className="text-sm text-slate-400 dark:text-slate-500 text-center mt-1">
                    {FOLDER_INFO[folder]?.description}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {filteredFiles.map(file => (
                  <FileCard
                    key={file.id}
                    file={file}
                    tripId={tripId}
                    token={token}
                    onDelete={handleDelete}
                    isOrganizer={isOrganizer}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
